/** @jsxImportSource @emotion/react */
'use memo'

import {
  ArrowRightOutlined,
  LockOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { SpotsTone } from '@sportninja/common/features/organization/registration/utils/availability'
import type {
  OrgRegistrationBadge,
  OrgRegistrationCardModel,
} from '@sportninja/common/features/organization/registration/utils/cardModel'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  captionXSmall,
  heading3,
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import ShareIcon from 'src/components/icons/ShareIcon'
import Button from 'src/components/ts-components/Button'
import { COUNTDOWN_COLORS, SPOTS_COLORS } from '../constants/tones'

interface Props {
  model: OrgRegistrationCardModel
  onOpen: () => void
  onShare: () => void
}

/**
 * The band under a card's identity row: when the registration closes (or
 * opens, or closed), how far through its window it is, the spots left, and
 * the actions. Full width, so the card has no dead space beside a tall panel.
 * A closed registration has nothing left to do, so its actions give way to a
 * "Registration Closed" pill of the same size — the strip keeps its shape and
 * the note stays centred where it is on every other card.
 */
const OrgRegistrationActionStrip = ({ model, onOpen, onShare }: Props) => {
  'use memo'

  const isOpen = model.badge === 'open' || model.badge === 'closingSoon'
  const isClosed = model.badge === 'past'

  return (
    <Strip>
      <Countdown>
        <CountdownLabel>{capitalizeWords(model.countdownLabel)}</CountdownLabel>
        <CountdownValue badge={model.badge}>
          {model.countdownValue}
        </CountdownValue>
        <CountdownDetail>{model.countdownDetail}</CountdownDetail>
      </Countdown>

      <Details>
        {isOpen ? (
          <>
            {model.windowProgress !== null && (
              <Window>
                <Track
                  role='progressbar'
                  aria-label={capitalizeWords('Registration window elapsed')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={model.windowProgress}
                >
                  <Fill
                    percent={model.windowProgress}
                    urgent={model.badge === 'closingSoon'}
                  />
                </Track>
                <WindowLabels>
                  <span>{model.windowStartLabel}</span>
                  <span>{model.windowEndLabel}</span>
                </WindowLabels>
              </Window>
            )}
            {model.spotsLabel && (
              <Spots tone={model.spots.tone}>
                <TeamOutlined aria-hidden='true' />
                {model.spotsLabel}
              </Spots>
            )}
          </>
        ) : (
          <Note>{model.note}</Note>
        )}

        {isClosed ? (
          <ClosedPill>
            <LockOutlined aria-hidden='true' />
            {capitalizeWords('Registration Closed')}
          </ClosedPill>
        ) : (
          <Actions>
            <Button
              text={capitalizeWords(isOpen ? 'Register Now' : 'View Details')}
              variant={isOpen ? 'primary' : 'outlined'}
              icon={<ArrowRightOutlined />}
              iconPosition='end'
              onClick={onOpen}
            />
            <Button
              iconOnly
              variant='outlined'
              text={capitalizeWords('Share Registration')}
              icon={<ShareIcon size={16} />}
              onClick={onShare}
            />
          </Actions>
        )}
      </Details>
    </Strip>
  )
}

export default OrgRegistrationActionStrip

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 32px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`

const Countdown = styled.div`
  flex: 0 0 auto;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const CountdownLabel = styled.span`
  ${captionXSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_60};
`

const CountdownValue = styled.span<{ badge: OrgRegistrationBadge }>`
  ${heading3}
  line-height: 1;
  text-transform: uppercase;
  color: ${({ badge }) => COUNTDOWN_COLORS[badge]};
`

const CountdownDetail = styled.span`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
`

const Details = styled.div`
  flex: 1 1 560px;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 32px;
`

const Window = styled.div`
  flex: 1 1 220px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Track = styled.div`
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: ${colors.SECONDARY_200};
`

const Fill = styled.div<{ percent: number; urgent: boolean }>`
  width: ${({ percent }) => percent}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ urgent }) => (urgent ? colors.WARNING_200 : colors.PRIMARY)};
`

const WindowLabels = styled.div`
  ${paragraphXSmall}
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: ${colors.NEUTRAL_60};
`

const Spots = styled.div<{ tone: SpotsTone }>`
  ${paragraphSmall}
  ${weightSemiBold}
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ tone }) => SPOTS_COLORS[tone]};
`

// Fills the space between the countdown and the actions, centred in it.
const Note = styled.div`
  ${paragraphSmall}
  flex: 1 1 220px;
  min-width: 0;
  text-align: center;
  color: ${colors.NEUTRAL_50};
`

/**
 * The action slot's width — the button group (Register Now + Share) and the
 * closed pill both take at least this, so every card's slot lines up.
 */
const ACTIONS_WIDTH = 224

const ClosedPill = styled.div`
  ${paragraphSmall}
  ${weightSemiBold}
  flex: 0 0 auto;
  min-width: ${ACTIONS_WIDTH}px;
  height: 40px;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-left: auto;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_300};
  color: ${colors.NEUTRAL_50};
`

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  min-width: ${ACTIONS_WIDTH}px;
  margin-left: auto;
`
